/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  AudioPresets,
  DefaultReconnectPolicy,
  type RoomOptions,
  type TrackPublishDefaults,
  VideoPreset,
  VideoPresets,
} from "livekit-client";

const defaultLiveKitPublishOptions: TrackPublishDefaults = {
  audioPreset: AudioPresets.music,
  dtx: true,
  // disable red because the livekit server strips out red packets for clients
  // that don't support it (firefox) but of course that doesn't work with e2ee.
  red: false,
  forceStereo: false,
  // simulcast OFF: a single high-quality layer. With simulcast ON, livekit-client
  // publishes the screenshare as a low-bitrate ladder (540p+1080p@5Mbps) using its OWN
  // presets and IGNORES screenShareEncoding below. Off -> our 1440p@20Mbps layer is used.
  simulcast: false,
  videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] as VideoPreset[],
  // THE decisive single-layer lever: sortPresets([]) -> [] so livekit-client never
  // appends its default half-res 540p layer. The old 2-layer 1080p ladder
  // [540p@1.2M + 1080p@5M] was livekit's STOCK screenshare simulcast presets, proving
  // our overrides weren't reaching computeVideoEncodings. This forces ONE layer even if
  // `simulcast` stays true anywhere upstream.
  screenShareSimulcastLayers: [] as VideoPreset[],
  // FLAT VideoEncoding (NOT a VideoPreset): for a screenshare track,
  // computeVideoEncodings reads options.screenShareEncoding and IGNORES videoEncoding.
  // 1440p60 @ 20Mbps, keep resolution over framerate under pressure.
  // Match Discord's measured screenshare: H264/VAAPI, ~8.6Mbps TARGET (uses ~1.4Mbps
  // on static content). High bitrate (20-40M) OVER-DROVE the VAAPI encoder -> frame
  // drops -> choppy. A modest target keeps the encoder near-idle so it holds full
  // resolution AND framerate = sharp + smooth.
  // BEST OVERALL (Discord-matched): ~10Mbps target keeps the VAAPI encoder unstressed so
  // it holds full 1440p AND 60fps = sharp + smooth. (maintain-framerate downscaled
  // constantly -> soft/terrible; high bitrate over-drove the encoder -> choppy.)
  screenShareEncoding: { maxBitrate: 10_000_000, maxFramerate: 60, priority: "high" },
  degradationPreference: "maintain-resolution",
  stopMicTrackOnMute: false,
  // H264 so AMD/VAAPI can HARDWARE-encode 1440p60 (browsers software-encode VP9 -> CPU stutter).
  // High bitrate (20Mbps) compensates for H264's lower efficiency. VP8 backup kept below.
  videoCodec: "h264",
  videoEncoding: VideoPresets.h720.encoding,
  backupCodec: { codec: "vp8", encoding: VideoPresets.h720.encoding },
} as const;

export const defaultLiveKitOptions: RoomOptions = {
  // automatically manage subscribed video quality
  adaptiveStream: true,

  // optimize publishing bandwidth and CPU for published tracks
  dynacast: true,

  // capture settings
  videoCaptureDefaults: {
    resolution: VideoPresets.h720.resolution,
  },

  // publish settings
  publishDefaults: defaultLiveKitPublishOptions,

  // default LiveKit options that seem to be sane
  stopLocalTrackOnUnpublish: true,
  reconnectPolicy: new DefaultReconnectPolicy(),
  disconnectOnPageLeave: true,
  webAudioMix: false,
};
